import { devError, devLog, isDevLoggingEnabled } from '@/lib/utils/devLog';

import type { AlgoliaDebugEntry } from './debugLog.types';

export type { AlgoliaDebugEntry, AlgoliaDebugPhase } from './debugLog.types';

const DEFAULT_HIT_SAMPLE_LIMIT = 5;

/**
 * Stack of active debug collections (supports nested runWith calls).
 * Note: not request-isolated under concurrent SSR — acceptable for dev-only tracing.
 */
const collectionStack: AlgoliaDebugEntry[][] = [];

function recordAlgoliaDebug(entry: Omit<AlgoliaDebugEntry, 'timestamp'>): void {
  const active = collectionStack[collectionStack.length - 1];
  if (!active) return;

  active.push({
    ...entry,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Run work while collecting Algolia debug entries for client-side echo.
 * No-op when dev logging is disabled.
 */
export async function runWithAlgoliaDebugCollection<T>(fn: () => Promise<T>): Promise<T> {
  if (!isDevLoggingEnabled()) {
    return fn();
  }

  const entries: AlgoliaDebugEntry[] = [];
  collectionStack.push(entries);

  try {
    return await fn();
  } finally {
    collectionStack.pop();
  }
}

/**
 * Collected Algolia debug entries for the active collection scope.
 */
export function getAlgoliaDebugEntries(): AlgoliaDebugEntry[] {
  return collectionStack[collectionStack.length - 1] ?? [];
}

/**
 * Sample object IDs from Algolia hits for concise debug output.
 */
export function summarizeObjectIds(
  hits: Array<{ objectID?: string | number } | null | undefined>,
  limit = DEFAULT_HIT_SAMPLE_LIMIT
): Array<string | number | null> {
  return hits.slice(0, limit).map((hit) => hit?.objectID ?? null);
}

/**
 * Log an Algolia operation request payload.
 */
export function logAlgoliaRequest(operation: string, details: Record<string, unknown>): void {
  recordAlgoliaDebug({ phase: 'request', operation, details });
  devLog(`[Algolia] ${operation} → request`, details);
}

/**
 * Log an Algolia operation response summary.
 */
export function logAlgoliaResponse(operation: string, details: Record<string, unknown>): void {
  recordAlgoliaDebug({ phase: 'response', operation, details });
  devLog(`[Algolia] ${operation} → response`, details);
}

/**
 * Log an Algolia operation error.
 */
export function logAlgoliaError(operation: string, details: Record<string, unknown>): void {
  recordAlgoliaDebug({ phase: 'error', operation, details });
  devError(`[Algolia] ${operation} → error`, details);
}

/**
 * Shared index/context fields included in most Algolia debug logs.
 */
export function algoliaContextFields(locale: string, indexName: string): Record<string, unknown> {
  return {
    indexName,
    locale,
    environment: process.env.ALGOLIA_ENVIRONMENT || 'production',
  };
}
